import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { useAuth } from 'react-oidc-context';
import { clearAuthState } from './auth/AuthConfig';
import { ROUTES } from '@/lib/routes';
import { LoadingInitializing } from '@/components/loadings/LoadingInitializing';
import { ErrorFallback } from '@/components/ErrorFallback';
import { AuthError } from '@/components/auth/AuthError';
import AuthCallback from './auth/AuthCallback';
import Unauthorized from './pages/Unauthorized';
import ProtectedRoute from './auth/ProtectedRoute';
import './index.css';

/* WHY: Lazy-load all page components so each route is a separate chunk.
   Auth-related components (AuthCallback, Unauthorized, ProtectedRoute) stay static
   because they're needed before any lazy component can load. */
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ExtractionForm = lazy(() => import('./pages/ExtractionForm'));
const DocExtraction = lazy(() => import('./pages/DocExtraction'));
const ConsentForm = lazy(() => import('./pages/ConsentForm'));
const ExtractionList = lazy(() => import('./pages/ExtractionList'));
const BatchProcessing = lazy(() => import('./pages/BatchProcessing'));
const Settings = lazy(() => import('./pages/Settings'));
const BatchHistory = lazy(() => import('./pages/BatchHistory'));
const BatchDetail = lazy(() => import('./pages/BatchDetail'));
const RecordDetail = lazy(() => import('./pages/RecordDetail'));
const MultiPageDetail = lazy(() => import('./pages/MultiPageDetail'));
const PreProcessing = lazy(() => import('./pages/PreProcessing'));
const PreProcessingHistory = lazy(() => import('./pages/PreProcessingHistory'));
const PreProcessingDetail = lazy(() => import('./pages/PreProcessingDetail'));
const NonProcessedFileDetail = lazy(() => import('./pages/NonProcessedFileDetail'));
const ProcessedFileDetail = lazy(() => import('./pages/ProcessedFileDetail'));

function App() {
  const auth = useAuth();

  useEffect(() => {
    if (auth.error && auth.error.message?.includes('No matching state')) {
      clearAuthState();
      window.location.reload();
    }
  }, [auth.error]);

  if (auth.isLoading) {
    return (
      <LoadingInitializing 
        message="Initializing Application"
        subMessage="Please wait while we set up your session..."
      />
    );
  }

  if (auth.error) {
    return (
      <AuthError
        title="Authentication Error"
        message="We encountered an issue while verifying your session. Please try again."
        buttonLabel="Clear Auth State & Retry"
        onRetry={() => { clearAuthState(); window.location.reload(); }}
      />
    );
  }

  return (
    <Router>
      <ErrorBoundary FallbackComponent={ErrorFallback} onError={(error, info) => { const msg = error instanceof Error ? error.message : String(error); console.error('Route error:', { message: msg, componentStack: info?.componentStack }); }}>
      <Suspense fallback={<LoadingInitializing message="Loading..." subMessage="Please wait..." />}>
        <Routes>
          <Route path={ROUTES.CALLBACK} element={<AuthCallback />} />
          <Route path={ROUTES.UNAUTHORIZED} element={<Unauthorized />} />
          <Route path={ROUTES.HOME} element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path={ROUTES.TRANSCRIPT_EXTRACTION} element={<ProtectedRoute><ExtractionForm /></ProtectedRoute>} />
          <Route path={ROUTES.DOC_EXTRACTION} element={<ProtectedRoute><DocExtraction /></ProtectedRoute>} />
          <Route path={ROUTES.CONSENT_EXTRACTION} element={<ProtectedRoute><ConsentForm /></ProtectedRoute>} />
          <Route path={ROUTES.BATCH_PROCESSING} element={<ProtectedRoute><BatchProcessing /></ProtectedRoute>} />
          <Route path={ROUTES.BATCH_HISTORY} element={<ProtectedRoute><BatchHistory /></ProtectedRoute>} />
          <Route path={ROUTES.BATCH_DETAIL} element={<ProtectedRoute><BatchDetail /></ProtectedRoute>} />
          <Route path={ROUTES.MULTI_PAGE_DETAIL} element={<ProtectedRoute><MultiPageDetail /></ProtectedRoute>} />
          <Route path={ROUTES.RECORD_DETAIL} element={<ProtectedRoute><RecordDetail /></ProtectedRoute>} />
          <Route path={ROUTES.EXTRACTION_HISTORY} element={<ProtectedRoute><ExtractionList /></ProtectedRoute>} />
          <Route path={ROUTES.SETTINGS} element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path={ROUTES.PRE_PROCESSING} element={<ProtectedRoute><PreProcessing /></ProtectedRoute>} />
          <Route path={ROUTES.PRE_PROCESSING_HISTORY} element={<ProtectedRoute><PreProcessingHistory /></ProtectedRoute>} />
          <Route path={ROUTES.PRE_PROCESSING_DETAIL} element={<ProtectedRoute><PreProcessingDetail /></ProtectedRoute>} />
          <Route path={ROUTES.NON_PROCESSED_FILE_DETAIL} element={<ProtectedRoute><NonProcessedFileDetail /></ProtectedRoute>} />
          <Route path={ROUTES.PROCESSED_FILE_DETAIL} element={<ProtectedRoute><ProcessedFileDetail /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
        </Routes>
      </Suspense>
      </ErrorBoundary>
    </Router>
  );
}

export default App;
